import { useMemo, useState } from 'react';
import CustomButton from '../../reusable/CustomButton';
import PrintIcon from '../../assets/print.svg';
import useFetchData from '../../hooks/useFetchData';
import { getFinalizedTranscriptsDetails } from '../../../services/transcriptsService';
import useSchools from '../../hooks/useSchools';
import { TRANSCRIPTS_CATEGORIES } from '../../../constants';
import { getSubjectAreas, getCollegeEligibilityFrameworks } from '../../../services/eoaConfigServices';
import PrintContainer from '../../reusable/PrintContainer';
import SubjectAreaTable from '../transcriptsBySubjectArea/SubjectAreaTable';
import { parseTranscriptsBySubjectArea, hasCoursesInSubjectArea } from '../transcriptsBySubjectArea/utils';
import TranscriptsCoverPage from './TranscriptsCoverPage';
import TranscriptsBySubjectAreaInstructionsPage from './TranscriptsBySubjectAreaInstructionsPage';
import TranscriptsCollegeEligibilityPage from './TranscriptsCollegeEligibilityPage';
import { SubjectAreaNames } from '../../../interfaces/EOAInterface';
import type { TranscriptsBySubjectAreaType } from '../../../interfaces/TranscriptsInterface';

const TranscriptsPrintBySubjectButton = () => {
  const { transcriptSelectionSchool, schools } = useSchools();
  const schoolName = schools.data?.find((s) => s.id === transcriptSelectionSchool)?.name ?? '';

  const [shouldFetch, setShouldFetch] = useState(false);

  const {
    isPending: isTranscriptsDataPending,
    error: transcriptsDataError,
    data: transcriptsData,
  } = useFetchData({
    fetchData: (eoaId, access_token) => getFinalizedTranscriptsDetails(eoaId, access_token, transcriptSelectionSchool),
    errorMessage: 'Error occurred while fetching transcripts details!',
    options: { dependencies: [shouldFetch] },
    initialState: {
      is_finalized: false,
      transcripts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: [],
      },
    },
  });

  const {
    isPending: isSubjectAreasDataPending,
    error: subjectAreasDataError,
    data: subjectAreasData,
  } = useFetchData({
    fetchData: (_eoaId, access_token) => getSubjectAreas(access_token),
    errorMessage: 'Error occurred while fetching subject areas!',
    options: { dependencies: [shouldFetch] },
    initialState: [],
  });

  const {
    isPending: isFrameworksPending,
    error: frameworksError,
    data: frameworksData,
  } = useFetchData({
    fetchData: (eoaId, access_token) => getCollegeEligibilityFrameworks(eoaId, access_token),
    errorMessage: 'Error occurred while fetching college eligibility frameworks!',
    options: { dependencies: [shouldFetch] },
    initialState: { frameworks: [] },
  });

  const parsedData = useMemo<TranscriptsBySubjectAreaType>(
    () =>
      !shouldFetch || isTranscriptsDataPending || isSubjectAreasDataPending
        ? ({} as TranscriptsBySubjectAreaType)
        : parseTranscriptsBySubjectArea(
            transcriptsData.transcripts,
            subjectAreasData.map(({ name }) => name)
          ),
    [shouldFetch, isTranscriptsDataPending, isSubjectAreasDataPending, transcriptsData, subjectAreasData]
  );

  if (transcriptsDataError || subjectAreasDataError || frameworksError) return <p>Print unavailable!</p>;

  const isFetching = shouldFetch && (isTranscriptsDataPending || isSubjectAreasDataPending || isFrameworksPending);
  const isContentReady = shouldFetch && !isTranscriptsDataPending && !isSubjectAreasDataPending && !isFrameworksPending;

  return (
    <PrintContainer
      isContentReady={isContentReady}
      onPrintRequested={() => setShouldFetch(true)}
      documentTitle="XQ EOAA transcripts (subject view)"
      printButton={(handlePrint) => (
        <CustomButton
          variant="text"
          buttonText="Print by subject"
          icon={<img src={PrintIcon} alt="print by subject" />}
          iconPosition="start"
          buttonAction={handlePrint}
          loading={isFetching}
          shouldShowUpdates={true}
        />
      )}
    >
      <>
        <TranscriptsCoverPage variant="by-subject-area" schoolName={schoolName} />
        <TranscriptsBySubjectAreaInstructionsPage />
        <TranscriptsCollegeEligibilityPage variant="by-subject-area" frameworks={frameworksData.frameworks} />
        {(Object.keys(parsedData) as SubjectAreaNames[]).map((subjectAreaName) =>
          hasCoursesInSubjectArea(parsedData[subjectAreaName]) ? (
            <div key={subjectAreaName} className="subject-area-page">
              <p>{subjectAreaName}</p>
              <SubjectAreaTable data={parsedData[subjectAreaName]} isPrint={true} />
            </div>
          ) : null
        )}
      </>
    </PrintContainer>
  );
};

export default TranscriptsPrintBySubjectButton;
